import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SgfiEvent, SgfiEventsService, sportImage, longDate } from "./sgfi-events.service";
import { GameService } from "../service/game.service";
import { ReportMeritService } from "../service/report-merit.service";
import { IssoUtilService } from "../../services/isso-util.service";

/**
 * The SGFI season, as a list of events.
 *
 * An event is a game + age group + gender - "U-19 Girls Football". Students are
 * selected into an event, and the event carries the last date, the optional
 * venue and reporting date, and any fee that differs from the standard one.
 */
@Component({
  selector: "app-sgfi-events",
  templateUrl: "./sgfi-events.component.html",
  styleUrls: ["./sgfi-events.component.css"],
})
export class SgfiEventsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  year = "";
  yearOptions: any[] = [];
  gameOptions: any[] = [];
  ageOptions: any[] = [];
  genderOptions: any[] = [];

  events: SgfiEvent[] = [];
  search = "";
  loading = true;
  error = "";
  success = "";

  /** The global SGFI fee, shown as what an empty override will charge. */
  standardFee = '';
  dialogOpen = false;
  saving = false;
  form = {
    gameId: "", ageRange: "", gender: "",
    lastDate: "", reportingDate: "", venue: "", description: "", feeOverride: "",
  };

  constructor(
    private api: SgfiEventsService,
    private router: Router,
    private gameService: GameService,
    private issoUtilService: IssoUtilService,
    private meritService: ReportMeritService,
  ) {}

  ngOnInit() {
    this.yearOptions = (this.issoUtilService.setYear() || []).filter(o => o.value !== "");
    this.ageOptions = (this.issoUtilService.setAge() || [])
      .filter(o => o.value !== "")
      .map(o => ({ label: "Under " + o.value, value: o.value }));
    this.genderOptions = (this.issoUtilService.setGender() || []).filter(o => o.value !== "");
    this.year = this.currentAcademicYear();

    this.gameService.getGameList().pipe(takeUntil(this.destroy$)).subscribe(
      (res: any) => {
        const list = Array.isArray(res) ? res : [];
        this.gameOptions = list.map(g => ({ label: g.gameName || g.name, value: String(g.gameId || g.id) }));
      },
      () => { /* The list still loads; only the picker is poorer. */ },
    );
    this.meritService.getAmountData().pipe(takeUntil(this.destroy$)).subscribe(
      (res: any) => {
        if (Array.isArray(res) && res.length) this.standardFee = String(res[0].amount || "");
      },
      () => { /* The placeholder falls back to naming no figure. */ },
    );
    this.load();
  }

  /** Names the figure an empty override will actually charge. */
  get feePlaceholder(): string {
    return this.standardFee ? "Standard fee (₹" + this.standardFee + ")" : "Standard fee";
  }

  /** June starts the next academic year - the same rule the server applies. */
  private currentAcademicYear(): string {
    const now = new Date();
    const y = now.getFullYear();
    return now.getMonth() + 1 >= 6 ? y + "-" + (y + 1) : (y - 1) + "-" + y;
  }

  load() {
    this.loading = true;
    this.error = "";
    this.api.list(this.year).pipe(takeUntil(this.destroy$)).subscribe(
      res => { this.loading = false; this.events = Array.isArray(res) ? res : []; },
      () => { this.loading = false; this.error = "Could not load the events. Please try again."; },
    );
  }

  /** Filtered in the browser: a season's events are a short list, not a page of them. */
  get shown(): SgfiEvent[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.events;
    return this.events.filter(e =>
      (this.title(e) + " " + (e.venue || "") + " " + (e.description || "")).toLowerCase().indexOf(q) >= 0);
  }

  genderLabel(g: string): string { return g === "1" ? "Boys" : g === "2" ? "Girls" : ""; }

  image(e: SgfiEvent): string { return sportImage(e.gameName); }

  day(iso: string): string { return longDate(iso); }

  /** "U-19 Girls Football", the way an admin says it out loud. */
  title(e: SgfiEvent): string {
    return "U-" + e.ageRange + " " + this.genderLabel(e.gender) + " " + (e.gameName || "");
  }

  open(e: SgfiEvent) { this.router.navigate(["/admin/sgfi-events", e.id]); }

  add() {
    this.form = { gameId: "", ageRange: "", gender: "", lastDate: "", reportingDate: "", venue: "", description: "", feeOverride: "" };
    this.error = ""; this.success = "";
    this.dialogOpen = true;
  }

  save() {
    if (this.saving) return;
    if (!this.form.gameId || !this.form.ageRange || !this.form.gender || !this.form.lastDate) {
      this.error = "Game, age group, gender and the last date to apply are all needed.";
      return;
    }
    this.saving = true;
    this.error = ""; this.success = "";
    const body: any = { year: this.year, ...this.form };
    this.api.save(body).pipe(takeUntil(this.destroy$)).subscribe(
      res => {
        this.saving = false;
        if (!res || res.status !== "success") { this.error = "Could not save the event. Please try again."; return; }
        this.success = "Event created.";
        this.dialogOpen = false;
        this.load();
      },
      err => {
        this.saving = false;
        this.error = (err && err.error && err.error.error) || "Could not save the event. Please try again.";
      },
    );
  }

  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
}
